import { TravelerEditMode } from "@apac/travelers";
import { Gender } from "@b2bportal/air-booking-api";
import {
  type Traveler,
  Gender as TravelerGender,
} from "@b2bportal/traveler-api";
import { getDateFormat } from "@hopper-b2b/i18n";
import { generateUserId, getDateTimeWithFormat } from "@hopper-b2b/utilities";
import { DriverLicenseFormFields } from "../hooks/useDriverLicenseFormFields";
import type { TravellerFormValues } from "../hooks/useTravellerForm";
import {
  convertPersonAssistance,
  convertTravelerAssistance,
  getTravelerGender,
} from "./convertPersonToTraveler";
import {
  getFrequentFlyerMap,
  type FrequentFlyerList,
} from "./frequentFlyerOptions";

const API_DATE_FORMAT = "YYYY-MM-DD";

const genderMap = {
  [Gender.M]: TravelerGender.Male,
  [Gender.F]: TravelerGender.Female,
};

type ConvertFormToPersonParams = {
  traveler: Partial<Traveler>;
  formValues: TravellerFormValues;
  frequentFlyerList: FrequentFlyerList;
  dateFormat?: string;
  travelerEditMode?: TravelerEditMode;
  applyDefaultGender?: boolean;
};

export const convertFormToTraveler = ({
  traveler,
  formValues,
  frequentFlyerList,
  dateFormat,
  travelerEditMode = TravelerEditMode.ALL,
}: ConvertFormToPersonParams): Traveler => {
  const {
    firstName,
    middleName,
    lastName,
    dateOfBirth,
    gender,
    assistanceRequired,
    passportIssuanceCountry,
    passportNumber,
    passportExpiryDate,
    nationality,
    redressNumber,
    travellerNumber,
    licenseAddress,
    licenseIssuingCity,
    licenseIssuingCountry,
    licensePostalCode,
  } = formValues;

  const preferedDateFormat = dateFormat || getDateFormat();

  const updatedTraveler: Traveler = {
    ...traveler,
    name: {
      first: firstName,
      middle: middleName,
      last: lastName,
    },
    id: traveler?.id || generateUserId(),
    dateOfBirth: getDateTimeWithFormat(dateOfBirth, preferedDateFormat).format(
      API_DATE_FORMAT
    ),
    // This field should be moved to the passport section, and be required for flights only
    airTravelerDetails: {
      ...(gender !== undefined && { gender: genderMap[gender] }),
    },
  };

  const airTravelerDetails = {
    passport: passportNumber
      ? {
          countryOfIssue: passportIssuanceCountry,
          number: passportNumber,
          expiration: getDateTimeWithFormat(
            passportExpiryDate,
            preferedDateFormat
          ).format(API_DATE_FORMAT),
        }
      : undefined,
    ...(nationality && { nationality: { country: nationality } }),
    ...(gender !== undefined && { gender: genderMap[gender] }),
    ...(travellerNumber && {
      knownTravelerNumber: { number: travellerNumber },
    }),
    ...(assistanceRequired != null && {
      assistance: convertPersonAssistance(assistanceRequired),
    }),
    ...(frequentFlyerList != null && {
      frequentFlyerNumbers: getFrequentFlyerMap(frequentFlyerList),
    }),
    ...(redressNumber &&
      redressNumber.length > 0 && {
        redressNumber: {
          number: redressNumber,
        },
      }),
  };

  if (
    travelerEditMode === TravelerEditMode.FLIGHTS_ONLY ||
    travelerEditMode === TravelerEditMode.ACTIVITIES_ONLY ||
    travelerEditMode === TravelerEditMode.ALL
  ) {
    updatedTraveler.airTravelerDetails = airTravelerDetails;
    updatedTraveler.groundTravelerDetails = undefined;
    updatedTraveler.hotelTravelerDetails = undefined;
  }

  if (
    travelerEditMode === TravelerEditMode.CARS_ONLY ||
    travelerEditMode === TravelerEditMode.ALL
  ) {
    updatedTraveler.groundTravelerDetails = {
      driverLicense: {
        country: licenseIssuingCountry,
        cityCode: licenseIssuingCity || "",
        street: licenseAddress || "",
        postalCode: licensePostalCode || "",
      },
    };
    updatedTraveler.hotelTravelerDetails = undefined;
  }

  if (
    travelerEditMode === TravelerEditMode.HOTELS_ONLY ||
    travelerEditMode === TravelerEditMode.ALL
  ) {
    updatedTraveler.groundTravelerDetails = undefined;
  }

  return updatedTraveler;
};

type GetTravellerFormValuesParams = {
  traveler?: Traveler;
  defaultValues: Partial<TravellerFormValues>;
  dateFormat?: string;
};

export const getTravellerFormValues = ({
  traveler,
  dateFormat,
  defaultValues,
}: GetTravellerFormValuesParams): Partial<TravellerFormValues> => {
  if (!traveler || !Object.keys(traveler).length) {
    return defaultValues;
  }

  const {
    name: { first: givenName, middle: middleName, last: surname },
    dateOfBirth,
  } = traveler;

  const {
    gender,
    passport,
    nationality,
    redressNumber,
    assistance,
    knownTravelerNumber,
  } = traveler.airTravelerDetails || {};

  const license = traveler?.groundTravelerDetails?.driverLicense;

  const preferedDateFormat = dateFormat || getDateFormat();
  const travelerGender = getTravelerGender(gender);
  const assistanceRequired = convertTravelerAssistance(assistance);

  return {
    ...defaultValues,
    firstName: givenName,
    middleName,
    lastName: surname,
    dateOfBirth: getDateTimeWithFormat(dateOfBirth, API_DATE_FORMAT).format(
      preferedDateFormat
    ),
    gender: travelerGender,
    assistanceRequired,
    passportIssuanceCountry: passport?.countryOfIssue,
    passportNumber: passport?.number,
    passportExpiryDate: passport?.expiration
      ? getDateTimeWithFormat(passport.expiration, API_DATE_FORMAT).format(
          preferedDateFormat
        )
      : undefined,
    nationality: nationality?.country,
    redressNumber: redressNumber?.number,
    travellerNumber: knownTravelerNumber?.number,
    [DriverLicenseFormFields.address]: license?.street,
    [DriverLicenseFormFields.issuingCity]: license?.cityCode,
    [DriverLicenseFormFields.issuingCountry]: license?.country,
    [DriverLicenseFormFields.postalCode]: license?.postalCode,
  };
};
